import {
  CORRECTION_STATUS_SUCCESS,
  CorrectionDataType,
  CorrectionLineResult,
} from '../../../services/payroll-correction.service';
import { NotificationService } from '../../../shared/services/notification.service';

/**
 * Columns shared by the line-level correction lists (Revert Adjustments, NAV Reset, Reprocess).
 * Processing status is shown from processingStatusText: ppl-list-table renders any column whose
 * field is `processingStatus` as a hyperlink.
 */
export const CORRECTION_LINE_COLUMNS = [
  { field: 'lineId', header: 'Line ID', jsonpath: 'lineId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'sourceType', header: 'Source', jsonpath: 'sourceType', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'employerId', header: 'Provider \nPPL ID', jsonpath: 'employerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'consumerId', header: 'Participant \nPPL ID', jsonpath: 'consumerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'serviceDate', header: 'Service \nDate', jsonpath: 'serviceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'serviceCode', header: 'Service \nCode', jsonpath: 'serviceCode', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'serviceCodeRate', header: 'Service \nRate', jsonpath: 'serviceCodeRate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'paidUnits', header: 'Paid \nUnits', jsonpath: 'paidUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'processingStatusText', header: 'Processing \nStatus', jsonpath: 'processingStatusText', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  { field: 'businessStatus', header: 'Application \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
];

/** Prepares a list row for CORRECTION_LINE_COLUMNS. */
export function toCorrectionLine(row: any): any {
  return { ...row, processingStatusText: row.processingStatus };
}

/**
 * Toast summarising a correction API's per-line results (STE = success, anything else = failed).
 * e.g. notifyCorrectionResults(svc, results, 'reverted') -> "3 line(s) reverted successfully."
 */
export function notifyCorrectionResults(
  notificationService: NotificationService,
  results: CorrectionLineResult[],
  verb: string,
  item = 'line(s)'
): void {
  const succeeded = results.filter(r => r.status === CORRECTION_STATUS_SUCCESS).length;
  const failed = results.length - succeeded;
  if (failed === 0) {
    notificationService.success('Success', `${succeeded} ${item} ${verb} successfully.`);
  } else if (succeeded === 0) {
    notificationService.error('Error', `${failed} ${item} could not be ${verb}.`);
  } else {
    const title = `Partially ${verb.charAt(0).toUpperCase()}${verb.slice(1)}`;
    notificationService.alert('warning', title, `${succeeded} ${item} ${verb}, ${failed} could not be ${verb}.`);
  }
}

/** The correction APIs take one dataType per call, so selected lines are sent grouped by their source (TS / IV). */
export function groupLineIdsByDataType(lines: any[]): Map<CorrectionDataType, number[]> {
  const idsByType = new Map<CorrectionDataType, number[]>();
  for (const line of lines) {
    const dataType: CorrectionDataType = line.sourceType === 'IV' ? 'IV' : 'TS';
    idsByType.set(dataType, [...(idsByType.get(dataType) ?? []), line.lineId]);
  }
  return idsByType;
}
